import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getAuth } from 'firebase/auth';

type Level = 'debug' | 'info' | 'warn' | 'error';

const ENABLE_API_LOG = import.meta.env.VITE_ENABLE_API_LOG === 'true';
const ENABLE_FIRESTORE_LOG = true; // Always enabled for direct Firestore logging

async function sendToFirestore(level: Level, type: string, extra?: unknown) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    // Skip logging to Firestore if user is not authenticated
    // Firestore rules require authentication for writing logs
    if (!user) {
      console.debug(`[Logger] Skipping Firestore log (no auth): ${level} ${type}`);
      return;
    }
    
    // Extract message and quizId from extra if available
    const message = typeof extra === 'object' && extra !== null && 'message' in extra 
      ? String((extra as any).message) 
      : null;
    const quizId = typeof extra === 'object' && extra !== null && 'quizId' in extra
      ? String((extra as any).quizId)
      : null;
    
    // Build log document, only including defined fields
    const logDoc: any = {
      level,
      type,
      uid: user.uid,
      clientTs: Date.now(),
      serverTs: serverTimestamp(),
    };
    
    // Only add optional fields if they have values
    if (message) logDoc.message = message;
    if (quizId) logDoc.quizId = quizId;
    if (extra) logDoc.extra = extra;
    
    await addDoc(collection(db, 'auditLogs'), logDoc);
  } catch (error) {
    // Silently fail for logging errors to avoid infinite loops
    console.warn('[Logger] Failed to write to Firestore:', error);
  }
}

async function sendToAPI(level: Level, message: string, extra?: unknown) {
  try {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    if (!token) return;
    
    const payload: any = { 
      level, 
      type: message,
      ts: Date.now() 
    };
    
    // Only add optional fields if they have values
    const extractedMessage = typeof extra === 'object' && extra !== null && 'message' in extra 
      ? String((extra as any).message) 
      : null;
    
    if (extractedMessage) payload.message = extractedMessage;
    if (extra) payload.extra = extra;
    
    await fetch('/api/log', {
      method: 'POST',
      headers: { 
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload),
    });
  } catch {
    // Swallow API logging errors
  }
}

async function send(level: Level, message: string, extra?: unknown) {
  // Always log to console in dev mode
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console[level === 'debug' ? 'log' : level]('[log]', message, extra ?? '');
  }
  
  // Log to Firestore (always enabled)
  if (ENABLE_FIRESTORE_LOG) {
    await sendToFirestore(level, message, extra);
  }
  
  // Also send to API if enabled (for production monitoring)
  if (ENABLE_API_LOG) {
    await sendToAPI(level, message, extra);
  }
}

export const log = {
  debug: (m: string, e?: unknown) => send('debug', m, e),
  info: (m: string, e?: unknown) => send('info', m, e),
  warn: (m: string, e?: unknown) => send('warn', m, e),
  error: (m: string, e?: unknown) => send('error', m, e),
};


